import * as Cesium from 'cesium';
/**
 * Convert area between units
 */
export const convertArea = (areaAcres, targetUnit) => {
    if (targetUnit === 'hectares') {
        return areaAcres * 0.404686; // acres to hectares
    }
    return areaAcres; // already in acres
};
/**
 * Calculate dynamic zoom altitude based on polygon area
 */
export const calculateZoomAltitude = (areaAcres) => {
    // Base calculation: 5km for 700 acres gives us a reference
    const referenceAcres = 700;
    const referenceAltitude = 5000; // meters
    // Use square root scaling to account for area vs linear distance relationship
    const scaleFactor = Math.sqrt(areaAcres / referenceAcres);
    // Calculate altitude with min/max bounds
    const calculatedAltitude = referenceAltitude * scaleFactor;
    const minAltitude = 500; // 500m minimum
    const maxAltitude = 50000; // 50km maximum
    return Math.max(minAltitude, Math.min(maxAltitude, calculatedAltitude));
};
/**
 * Sample terrain heights at multiple points within bounds and return the maximum height
 */
const sampleTerrainHeights = async (viewer, bounds) => {
    if (!viewer || !viewer.terrainProvider) {
        return 0; // Return 0 if no terrain provider available
    }
    const samplePoints = [];
    if (Array.isArray(bounds)) {
        // For polygon bounds, sample at vertices and center
        bounds.forEach(coord => {
            samplePoints.push(Cesium.Cartographic.fromDegrees(coord.lng, coord.lat));
        });
        // Add center point
        const centerLat = bounds.reduce((sum, coord) => sum + coord.lat, 0) / bounds.length;
        const centerLng = bounds.reduce((sum, coord) => sum + coord.lng, 0) / bounds.length;
        samplePoints.push(Cesium.Cartographic.fromDegrees(centerLng, centerLat));
    }
    else {
        // For rectangular bounds, sample at corners and center
        const { west, south, east, north } = bounds;
        samplePoints.push(Cesium.Cartographic.fromDegrees(west, south), Cesium.Cartographic.fromDegrees(east, south), Cesium.Cartographic.fromDegrees(east, north), Cesium.Cartographic.fromDegrees(west, north), Cesium.Cartographic.fromDegrees((west + east) / 2, (south + north) / 2));
    }
    try {
        // Sample terrain heights at these points
        const sampledPoints = await Cesium.sampleTerrainMostDetailed(viewer.terrainProvider, samplePoints);
        // Find the maximum height
        const maxHeight = sampledPoints.reduce((max, point) => {
            const height = point.height || 0;
            return Math.max(max, height);
        }, 0);
        return Math.max(0, maxHeight); // Ensure non-negative
    }
    catch (error) {
        console.warn('Failed to sample terrain heights:', error);
        return 0; // Fallback to sea level
    }
};
/**
 * Calculate terrain-aware camera altitude that ensures good visibility above ground
 */
export const calculateTerrainAwareAltitude = async (viewer, bounds, areaAcres) => {
    // Get base altitude from area calculation
    const baseAltitude = areaAcres ? calculateZoomAltitude(areaAcres) : 5000;
    // Sample terrain heights in the area
    const maxTerrainHeight = await sampleTerrainHeights(viewer, bounds);
    // Calculate minimum safe altitude above terrain
    // Use a buffer of at least 200m above highest terrain, but scale with area
    const terrainBuffer = Math.max(200, baseAltitude * 0.1);
    const minAltitudeAboveTerrain = maxTerrainHeight + terrainBuffer;
    // Ensure camera is high enough above terrain for good perspective
    const finalAltitude = Math.max(baseAltitude, minAltitudeAboveTerrain);
    // Apply absolute bounds
    const minAltitude = Math.max(500, maxTerrainHeight + 100); // At least 100m above terrain
    const maxAltitude = 50000; // 50km maximum
    return Math.max(minAltitude, Math.min(maxAltitude, finalAltitude));
};
/**
 * Format area for display
 */
export const formatArea = (areaAcres, unit) => {
    const displayArea = convertArea(areaAcres, unit);
    const unitText = unit === 'hectares' ? 'ha' : 'acres';
    return `${displayArea.toFixed(2)} ${unitText}`;
};
